import {
  formatDurationMs,
  parseIsoMs,
} from "@/features/opportunities/lib/format-relative-time";
import type { EventJobItem } from "@/features/events/api/events.api";

export interface RunDurationDisplay {
  durationText: string;
  timedOut: boolean;
}

/**
 * Whether a live run has been processing longer than the operator says is
 * normal. `capMs` is `eventsStuckAfterMinutes` from the ops settings — the
 * frontend no longer keeps a deadline of its own, because a number invented
 * here was never anything but a guess about somebody else's infrastructure.
 */
export function isEventProcessingTimedOut(
  row: Pick<EventJobItem, "status" | "processingStartedAt">,
  now: number,
  capMs: number,
): boolean {
  if (row.status !== "PROCESSING") return false;
  const start = parseIsoMs(row.processingStartedAt);
  if (start == null) return false;
  return now - start >= capMs;
}

export function getRunDurationDisplay(
  row: EventJobItem,
  now: number,
  capMs: number,
): RunDurationDisplay {
  if (row.runDurationMs != null) {
    return { durationText: formatDurationMs(row.runDurationMs), timedOut: false };
  }

  const start = parseIsoMs(row.processingStartedAt);
  if (start == null) return { durationText: "-", timedOut: false };

  if (row.status === "PROCESSING") {
    if (isEventProcessingTimedOut(row, now, capMs)) {
      return {
        durationText: `${formatDurationMs(capMs)} (timed out)`,
        timedOut: true,
      };
    }
    return { durationText: formatDurationMs(Math.max(0, now - start)), timedOut: false };
  }

  const end = parseIsoMs(row.processingCompletedAt);
  if (end == null) return { durationText: "-", timedOut: false };
  return { durationText: formatDurationMs(Math.max(0, end - start)), timedOut: false };
}

export function hasActiveProcessingEventJobs(
  rows: Array<Pick<EventJobItem, "status" | "processingStartedAt">>,
  now: number,
  capMs: number,
): boolean {
  return rows.some(
    (row) => row.status === "PROCESSING" && !isEventProcessingTimedOut(row, now, capMs),
  );
}
